import React from 'react';
import { 
  Crown, Trophy, Users, Calendar, BarChart3, 
  Heart, Target, Zap, Star, Award, TrendingUp,
  Mail, Linkedin, Code, Settings
} from 'lucide-react';

const About = ({ h2hData = [] }) => {
  // Calculate years running dynamically
  const getYearsRunning = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-indexed, so September is 8
    
    // League started in 2011, so base calculation from there
    const startYear = 2011;
    let yearsRunning = currentYear - startYear;
    
    // If we haven't reached September 1st yet, subtract 1 from the count
    if (currentMonth < 8) { // Before September
      yearsRunning -= 1;
    }
    
    return `${yearsRunning}+`;
  };

  // Calculate total games dynamically from h2hData
  const getTotalGames = () => {
    if (!h2hData || h2hData.length === 0) return 'Loading...';
    
    try {
      // Get maximum GameID value
      const gameIds = h2hData
        .map(game => parseInt(game.GameID))
        .filter(id => !isNaN(id));
      
      if (gameIds.length === 0) return '0';
      
      const maxGameId = Math.max(...gameIds);
      return maxGameId.toLocaleString('en-US');
    } catch (error) {
      console.error('Error calculating total games:', error);
      return '0';
    }
  };

  // Calculate total weeks dynamically from h2hData
  const getTotalWeeks = () => {
    if (!h2hData || h2hData.length === 0) return 'Loading...';
    
    try {
      // Get maximum LeagueWeek value
      const leagueWeeks = h2hData
        .map(game => parseInt(game.LeagueWeek))
        .filter(week => !isNaN(week));
      
      if (leagueWeeks.length === 0) return '0';
      
      const maxLeagueWeek = Math.max(...leagueWeeks);
      return maxLeagueWeek.toLocaleString('en-US');
    } catch (error) {
      console.error('Error calculating total weeks:', error);
      return '0';
    }
  };

  const stats = [
    { label: 'Years Running', value: getYearsRunning(), icon: Calendar },
    { label: 'Total Games', value: getTotalGames(), icon: Trophy },
    { label: 'Total Weeks', value: getTotalWeeks(), icon: BarChart3 }
  ];

  const founders = [
    { name: 'Kevin', extension: 'jpg', position: 'object-center' },
    { name: 'Jake', extension: 'png', position: 'object-top' },
    { name: 'Lyons', extension: 'png', position: 'object-center' },
    { name: 'Nipples', extension: 'png', position: 'object-top' },
    { name: 'Dan', extension: 'png', position: 'object-center' },
    { name: 'Shuds', extension: 'png', position: 'object-center' },
    { name: 'Karp', extension: 'png', position: 'object-center' },
    { name: 'Mills', extension: 'png', position: 'object-center' },
    { name: 'Joey', extension: 'png', position: 'object-center' },
    { name: 'Raffy', extension: 'png', position: 'object-center' },
    { name: 'Oster', extension: 'png', position: 'object-center' },
    { name: 'Scott', extension: 'png', position: 'object-center' },
    { name: 'Sam', extension: 'png', position: 'object-center' }
  ];

  // Current champion and sacko (calculate from h2hData)
  const getCurrentChampionAndSacko = () => {
    if (!h2hData || h2hData.length === 0) {
      console.log('No h2hData available for champion/sacko calculation');
      return { currentChampion: null, currentSacko: null };
    }

    try {
      // Get the most recent year from the data
      const years = h2hData.map(game => game.Year).filter(year => year && !isNaN(year));
      const mostRecentYear = Math.max(...years);
      console.log('Most recent year:', mostRecentYear);

      // Debug: Check what game values exist
      const gameValues = [...new Set(h2hData.map(game => game.Game))];
      console.log('Available game values:', gameValues);

      // Debug: Check games in most recent year
      const recentYearGames = h2hData.filter(game => game.Year === mostRecentYear);
      console.log('Games in most recent year:', recentYearGames.length);
      
      // Look for championship and sacko games
      const championshipGames = recentYearGames.filter(game => 
        game.Game === 'League Championship'
      );
      console.log('League Championship games:', championshipGames);

      const sackoGames = recentYearGames.filter(game => 
        game.Game === '11th Place Game'
      );
      console.log('11th Place Game games:', sackoGames);

      // Find League Championship winner in most recent year
      const championshipGame = h2hData.find(game => 
        game.Year === mostRecentYear && 
        game.Game === 'League Championship' && 
        game.Outcome === 'Win'
      );

      // Find 11th Place Game loser in most recent year (sacko)
      const sackoGame = h2hData.find(game => 
        game.Year === mostRecentYear && 
        game.Game === '11th Place Game' && 
        game.Outcome === 'Loss'
      );

      console.log('Championship game found:', championshipGame);
      console.log('Sacko game found:', sackoGame);

      const result = {
        currentChampion: championshipGame?.Manager1 || null,
        currentSacko: sackoGame?.Manager1 || null
      };
      
      console.log('Final result:', result);
      return result;
    } catch (error) {
      console.error('Error calculating champion and sacko:', error);
      return { currentChampion: null, currentSacko: null };
    }
  };

  const { currentChampion, currentSacko } = getCurrentChampionAndSacko();

  // Randomize founder order (but keep it consistent during the component lifecycle)
  const [randomizedFounders] = React.useState(() => {
    const shuffled = [...founders];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-8 lg:space-y-12">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 rounded-3xl shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="relative px-6 sm:px-8 lg:px-12 py-12 lg:py-16">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/50 rounded-full blur-xl animate-pulse"></div>
                <img 
                  src="/beasthead.png" 
                  alt="Beasts of Bellevue" 
                  className="relative h-20 w-20 sm:h-24 sm:w-24 lg:h-28 lg:w-28 object-contain drop-shadow-2xl"
                />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 lg:mb-6 leading-tight">
              <span className="bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500 bg-clip-text text-transparent">
                Beasts of Bellevue
              </span>
            </h1>
            
            <div className="relative">
              <p className="text-lg sm:text-xl lg:text-2xl text-purple-100 font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
                The greatest sports entity to emerge from Sherwood High School since Scott Van Pelt
              </p>
              
              {/* SVP Looking Up - moved to bottom right, below the text */}
              <div className="absolute -bottom-16 sm:-bottom-20 lg:-bottom-24 right-0 sm:right-4 lg:right-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-blue-400/30 rounded-full blur-2xl animate-pulse"></div>
                  <img 
                    src="/svp.png" 
                    alt="Scott Van Pelt" 
                    className="relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 object-contain transform -scale-x-100 rotate-6 opacity-85 hover:opacity-100 transition-all duration-300 hover:scale-105 cursor-pointer drop-shadow-2xl"
                  />
                  {/* Thought bubble - adjusted position for new location */}
                  <div className="absolute -top-8 sm:-top-10 lg:-top-12 -right-2 sm:-right-4 bg-white/95 backdrop-blur-sm rounded-2xl px-2 sm:px-3 py-1 text-xs sm:text-sm text-gray-800 font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg pointer-events-none">
                    "Hold up..."
                  </div>
                </div>
              </div>
            </div>
            
            <div className="inline-flex items-center space-x-2 bg-yellow-400/20 backdrop-blur-sm rounded-full px-6 py-3 border border-yellow-400/30">
              <Crown className="h-5 w-5 text-yellow-400" />
              <span className="text-yellow-200 font-semibold">Est. 2012</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.label}
              className="bg-white rounded-2xl shadow-xl p-6 text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full p-3">
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-black text-gray-900 mb-2">{stat.value}</div>
              <div className="text-sm font-medium text-gray-600">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Olney Beast Image - Large Standalone with Moody Lighting */}
      <div className="flex justify-center py-8">
        <div className="relative group">
          {/* Multiple layered background effects for atmospheric lighting */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-orange-500/30 to-red-600/20 rounded-3xl blur-3xl animate-pulse group-hover:from-purple-600/30 group-hover:via-orange-500/40 group-hover:to-red-600/30 transition-all duration-700 scale-110"></div>
          
          {/* Secondary glow layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-400/25 via-orange-600/35 to-red-700/25 rounded-2xl blur-2xl animate-pulse group-hover:from-amber-400/35 group-hover:via-orange-600/45 group-hover:to-red-700/35 transition-all duration-500 scale-105" style={{animationDelay: '0.5s'}}></div>
          
          {/* Spotlight effect from top */}
          <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 w-32 h-32 bg-yellow-300/20 rounded-full blur-2xl group-hover:bg-yellow-300/30 transition-all duration-700"></div>
          
          {/* Side rim lighting */}
          <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-4 h-3/4 bg-gradient-to-r from-purple-500/40 to-transparent rounded-full blur-xl group-hover:from-purple-500/60 transition-all duration-500"></div>
          <div className="absolute -right-8 top-1/2 transform -translate-y-1/2 w-4 h-3/4 bg-gradient-to-l from-red-500/40 to-transparent rounded-full blur-xl group-hover:from-red-500/60 transition-all duration-500"></div>
          
          {/* Floating particles effect */}
          <div className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity duration-700">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0s'}}></div>
            <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-orange-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-red-400 rounded-full animate-ping" style={{animationDelay: '2s'}}></div>
            <div className="absolute top-2/3 right-1/3 w-1 h-1 bg-purple-400 rounded-full animate-ping" style={{animationDelay: '1.5s'}}></div>
          </div>
          
          {/* Main image with enhanced shadow and lighting */}
          <div className="relative bg-gradient-to-br from-black/10 via-transparent to-black/10 rounded-2xl p-2">
            <img 
              src="/olneybeast.png" 
              alt="Olney Beast" 
              className="relative w-64 h-48 sm:w-80 sm:h-60 lg:w-96 lg:h-72 xl:w-[32rem] xl:h-80 object-contain rounded-2xl group-hover:scale-105 transition-all duration-700 filter group-hover:brightness-110 group-hover:contrast-105"
              style={{
                dropShadow: '0 25px 50px rgba(0,0,0,0.5)',
                filter: 'drop-shadow(0 25px 50px rgba(255,165,0,0.3)) drop-shadow(0 0 30px rgba(255,69,0,0.2))'
              }}
            />
            
            {/* Subtle overlay gradient for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/10 rounded-2xl pointer-events-none opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
          </div>
          
          {/* Bottom reflection/glow */}
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-3/4 h-8 bg-gradient-to-t from-orange-500/30 to-transparent rounded-full blur-xl group-hover:from-orange-500/40 transition-all duration-500"></div>
        </div>
      </div>

      {/* Story Section */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* Standalone Hero Image */}
        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden">
          {/* Atmospheric background effects only */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/15 via-blue-500/15 to-indigo-600/15"></div>
          <div className="absolute -top-12 -left-12 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-blue-500/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
          
          {/* Clean, unobstructed hero image */}
          <img 
            src="/sherwood.png" 
            alt="Sherwood High School" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section - Completely Separate */}
        <div className="px-8 lg:px-12 py-12 lg:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
                Our Story
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-indigo-600 mx-auto rounded-full mb-6"></div>
              <p className="text-xl text-gray-600 font-medium max-w-3xl mx-auto">
                From the hallways of Sherwood High School to the digital gridiron
              </p>
            </div>

            {/* Story Content with Woven Images */}
            <div className="space-y-12">
              {/* First Story Block */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                  What happens when you take a pack of goofy knuckleheads from Sherwood High School in Olney, MD, 
                  give them access to the internet, and the devastating realization that their athletic careers peaked in little league? You get the Beasts of Bellevue - a fantasy football empire built by aging desk jockeys who, having watched their once-mighty bodies betray them one pulled hamstring at a time, now channel their fading dreams of sports glory into obsessively micromanaging millionaire athletes they'll never meet. We're basically the most accomplished sports entity to emerge from Sherwood since Scott Van Pelt walked into ESPN Studios.
                  </p>
                  
                  <p className="text-lg text-gray-700 leading-relaxed">
                    The roots of this madness actually stretch back to somewhere between 2000-2004, when we were 
                    just middle schoolers and early high schoolers, when the Internet was a place to which you could arrive and from which you could depart, and if your houseline got a phone call, you lost access to your fantasy lineup.
                  </p>
                </div>

                {/* Sherwood Fellas Image */}
                <div className="flex justify-center lg:justify-end">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-xl group-hover:bg-blue-500/30 transition-all duration-500"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400/15 to-green-500/15 rounded-2xl blur-lg animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    
                    <div className="relative bg-gradient-to-br from-white/20 to-transparent rounded-2xl p-2">
                      <img 
                        src="/sherwoodfellas.png" 
                        alt="Sherwood Fellas" 
                        className="relative h-80 sm:h-96 lg:h-[28rem] w-auto object-contain rounded-xl shadow-2xl group-hover:scale-105 transition-all duration-500 filter group-hover:brightness-105 transform rotate-90"
                        style={{
                          filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.2)) drop-shadow(0 0 20px rgba(59,130,246,0.1))'
                        }}
                      />
                      
                      {/* Subtle overlay for depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 rounded-xl pointer-events-none"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Story Block */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Modern Fellas Image - Left Side */}
                <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-orange-500/20 rounded-2xl blur-xl group-hover:bg-orange-500/30 transition-all duration-500"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400/15 to-red-500/15 rounded-2xl blur-lg animate-pulse"></div>
                    
                    <div className="relative bg-gradient-to-br from-white/20 to-transparent rounded-2xl p-2">
                      <img 
                        src="/fellas.png" 
                        alt="The Fellas" 
                        className="relative w-full max-w-xs object-contain rounded-xl shadow-2xl group-hover:scale-105 transition-all duration-500 filter group-hover:brightness-105"
                        style={{
                          filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.2)) drop-shadow(0 0 20px rgba(255,165,0,0.1))'
                        }}
                      />
                      
                      {/* Subtle overlay for depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 rounded-xl pointer-events-none"></div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 order-1 lg:order-2">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    But like most good things that start as casual fun, we didn't get serious about ruining our 
                    friendships until 2012, when we officially launched what would become a full-scale analytical 
                    war machine. The "Bellevue" name pays homage to Bellevue Street in Dewey Beach, where the 
                    legendary Karp family beach house served as ground zero for all manner of youthful indiscretion. The capacity for dubious decision-making still lives in all the Beasts, but now it takes the form of keeper decisions, draft night blunders, and fall Sunday afternoon regret.
                  </p>
                  
                  <p className="text-lg text-gray-700 leading-relaxed">
                  Beyond the spreadsheets and smack talk, this league has become the thread that keeps us woven together across time zones, career changes, and life's inevitable curveballs. Every season memorializes another year of friendship, and generates a new batch of premium roasting material. In the Beasts' world, there's no greater glory than wearing the Beast Crown for a full year—just ask {currentChampion || 'our reigning champion'}, who gets to lord his superiority over the rest of the beast peasants until next season. And there's no shame quite like donning the Sacko Cap, a burden currently carried by {currentSacko || 'our beloved last-place finisher'}, who serves as a constant reminder that in fantasy football, as in life, someone has to finish last. This isn't just a league, it's a time capsule of friendship, sealed with love and mockery. Mostly mockery.

                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* League Commandments - Clean Footer Section */}
        <div className="bg-gradient-to-br from-gray-50 to-purple-50 px-8 lg:px-12 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div className="relative group">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg group-hover:bg-orange-500/40 transition-all duration-300"></div>
                  <img 
                    src="/beastcommandments.png" 
                    alt="Beast Commandments" 
                    className="relative h-32 sm:h-40 lg:h-48 object-contain drop-shadow-lg group-hover:scale-105 transition-all duration-300"
                  />
                </div>
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
                League Commandments
              </h3>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="text-3xl mb-3">🌮</div>
                <div className="font-semibold text-gray-900 mb-2">Don't Be a Taco</div>
                <div className="text-gray-600 text-sm">Set your damn lineup</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="flex justify-center mb-3">
                  <Heart className="h-8 w-8 text-red-500" />
                </div>
                <div className="font-semibold text-gray-900 mb-2">Scott's Law</div>
                <div className="text-gray-600 text-sm">Kickers are here to be paid</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="flex justify-center mb-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <div className="font-semibold text-gray-900 mb-2">Jake's Theory of Constant Motion</div>
                <div className="text-gray-600 text-sm">If you're not maxing out your weekly transactions, you're not trying hard enough to fail.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Founders Section */}
      <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 rounded-3xl shadow-2xl p-8 lg:p-12">
        <div className="text-center mb-8 lg:mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            The Men Behind The Beasts
          </h2>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
Playacting as football coaches has never looked so good.          </p>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
          {randomizedFounders.map((founder, index) => {
            const isChampion = founder.name === currentChampion;
            const isSacko = founder.name === currentSacko;
            const isFormerMember = founder.name === 'Sam';
            
            return (
              <div 
                key={founder.name}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/20 relative group"
              >
                <div className="w-20 h-20 mx-auto mb-3 relative">
                  <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-md animate-pulse"></div>
                  <img 
                    src={`/${founder.name.toLowerCase()}real.${founder.extension}`}
                    alt={`${founder.name} - League Founder`}
                    className={`relative w-20 h-20 rounded-full object-cover border-3 border-yellow-400/60 shadow-xl ${founder.position} ${isFormerMember ? 'grayscale' : ''}`}
                  />
                  
                  {/* Champion Crown */}
                  {isChampion && (
                    <div className="absolute -top-3 -right-2 z-20">
                      <div className="relative">
                        <div className="absolute inset-0 bg-yellow-400/40 rounded-full blur-sm animate-pulse group-hover:bg-yellow-400/60 transition-all duration-300"></div>
                        <img 
                          src="/thebeastcrown.png" 
                          alt="Champion Crown" 
                          className="relative w-10 h-10 object-contain drop-shadow-lg group-hover:w-40 group-hover:h-40 group-hover:-translate-x-16 group-hover:-translate-y-16 transition-all duration-500 transform group-hover:scale-400"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Sacko Cap */}
                  {isSacko && (
                    <div className="absolute -top-3 -right-2 z-20">
                      <div className="relative">
                        <div className="absolute inset-0 bg-red-400/40 rounded-full blur-sm animate-pulse group-hover:bg-red-400/60 transition-all duration-300"></div>
                        <img 
                          src="/sackocap.png" 
                          alt="Sacko Cap" 
                          className="relative w-10 h-10 object-contain drop-shadow-lg group-hover:w-40 group-hover:h-40 group-hover:-translate-x-16 group-hover:-translate-y-16 transition-all duration-500 transform group-hover:scale-400"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-white font-bold text-sm lg:text-base">{founder.name}</div>
                {isFormerMember && (
                  <div className="text-gray-400 text-xs mt-1 italic">Former member</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-gray-900 rounded-3xl shadow-2xl p-8 lg:p-12 relative overflow-hidden">
        {/* Championship Background Image - Positioned as atmospheric design element */}
        <div className="absolute inset-0 opacity-20 sm:opacity-25 md:opacity-30 lg:opacity-35">
          <div className="absolute top-0 right-0 w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 xl:w-[28rem] xl:h-[28rem]">
            <img 
              src="/kevinchamp3.png" 
              alt="Kevin Championship Moment" 
              className="w-full h-full object-contain object-top-right opacity-70 sm:opacity-75 md:opacity-80 transform rotate-6 sm:rotate-8 md:rotate-10 lg:rotate-12 scale-105 sm:scale-108 md:scale-110 blur-[1px] hover:blur-0 hover:opacity-90 sm:hover:opacity-95 md:hover:opacity-100 transition-all duration-700"
            />
            {/* Additional atmospheric effects */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-yellow-500/5 sm:via-yellow-500/8 md:via-yellow-500/10 to-purple-900/70 sm:to-purple-900/65 md:to-purple-900/60"></div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-6 sm:mb-8 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 drop-shadow-lg">
              Behind the Madness
            </h2>
            <p className="text-purple-200 text-base sm:text-lg max-w-2xl mx-auto px-2 sm:px-0">
              Meet the commissioner/developer who somehow convinced himself this was a good use of time
            </p>
          </div>
          
          <div className="bg-white/15 backdrop-blur-xl rounded-2xl p-4 sm:p-6 lg:p-8 border border-white/30 shadow-2xl">
            <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-4 sm:space-y-6 lg:space-y-0 lg:space-x-8">
              <div className="flex-shrink-0">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/50 to-yellow-500/50 rounded-full blur-lg animate-pulse"></div>
                  <div className="absolute inset-0 bg-yellow-400/30 rounded-full blur-md animate-pulse" style={{animationDelay: '0.5s'}}></div>
                  <img 
                    src="/kevinreal.jpg" 
                    alt="Kevin Klein - Commissioner & Developer" 
                    className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full object-cover border-4 border-yellow-400/70 shadow-2xl hover:scale-105 transition-all duration-300"
                  />
                  {/* Championship indicator */}
                  <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-yellow-400 rounded-full p-1 shadow-lg">
                    <Crown className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-900" />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 drop-shadow-md">Kevin Klein</h3>
                <div className="flex flex-wrap justify-center lg:justify-start gap-1 sm:gap-2 mb-3 sm:mb-4">
                  <span className="inline-flex items-center space-x-1 bg-yellow-400/25 text-yellow-200 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm font-medium border border-yellow-400/30">
                    <Settings className="h-2 w-2 sm:h-3 sm:w-3" />
                    <span>League Commissioner</span>
                  </span>
                  <span className="inline-flex items-center space-x-1 bg-blue-400/25 text-blue-200 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm font-medium border border-blue-400/30">
                    <Code className="h-2 w-2 sm:h-3 sm:w-3" />
                    <span>Site Developer</span>
                  </span>
                  <span className="inline-flex items-center space-x-1 bg-green-400/25 text-green-200 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm font-medium border border-green-400/30">
                    <Trophy className="h-2 w-2 sm:h-3 sm:w-3" />
                    <span>3x Champion</span>
                  </span>
                </div>
                
                <p className="text-purple-100 mb-4 sm:mb-6 leading-relaxed drop-shadow-sm text-sm sm:text-base">
                  When Kevin isn't busy counting his championships, he's building analytics platforms to elaborately remind his leaguemates he has them. As both commissioner and developer, he maintains the delicate 
                  balance of absolute power and zero accountability that makes fantasy sports so beautiful.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                  <a 
                    href="mailto:kevinklein333@gmail.com"
                    className="inline-flex items-center justify-center space-x-2 bg-white/25 hover:bg-white/35 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 transform hover:scale-105 border border-white/30 backdrop-blur-sm shadow-lg"
                  >
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">kevinklein333@gmail.com</span>
                    <span className="sm:hidden">Email</span>
                  </a>
                  
                  <a 
                    href="https://www.linkedin.com/in/kevinkleinads"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center space-x-2 bg-blue-600/80 hover:bg-blue-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 transform hover:scale-105 backdrop-blur-sm shadow-lg"
                  >
                    <Linkedin className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-purple-200 text-xs sm:text-sm drop-shadow-sm px-2 sm:px-0">
              Questions about the league? Want to build something similar?  
              <br className="hidden sm:block" />
            Drop Kevin a line.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;